/* MODEL ORCHESTRATOR - IS/BS/CFS with iterative sweep convergence
   Circular ref: CFS sweep interest → IS oi_interest, sweep balances → BS cash lines
   Iterates until money market delta < $0.01 */

import { computeIS, r2 } from './computeIS';
import { computeBS } from './computeBS';
import { computeCFS } from './computeCFS';
import { revenueInjections } from './revenueByLeaf';   // 2026-10-27: the ONE owner of the revenue injection (AE / CQ / implementation)
import { implDrSyntheticKey, implDrRefKey, implDrBsKey } from '../utils/implKeys';
import { daLeafIds } from './daRouting';
import { ACCRUED_PAYROLL_REF, accruedPayrollBuiltKey } from './accruedPayroll';   // bi-weekly payroll, step 3b
import { ACCRUED_COMMISSIONS_REF, accruedCommissionsBuiltKey } from './accruedCommissions';   // commission ship 4
import { UNBILLED_AR_REF, unbilledArBuiltKey } from './billingTerms';   // billing terms P2: the Unbilled-AR link (p)

const MAX_ITER = 20;
const THRESHOLD = 0.01;

// ★ SWEEP B2-ii: the hardcoded 2024-2026 ALL_M_DEFAULT window is DELETED. It was Semitek's
// model window used as a back-compat default; every caller passes allMonths
// (runFullModel always does) and no page imports the export — verified. An
// absent window now yields an empty one (honest-empty), not a foreign calendar.

// Phase 3b: module-link refKey registry. Each refKey (consumed by computeBS
// via `line.module_link_ref` → `IS_REF[refKey]`) maps to a function that
// extracts the per-month value from the upstream engine outputs. This is
// the single source of truth that buildBSIsRef writes to and computeBS
// reads from — replaces the previous parallel-hardcoding split between
// orchestrator and the computeBS if-chain. Phase 8 will generalize further
// (defrev buckets become per-tenant configurable).
function refKeyGetters(prepaidBalance, defRevResult) {
  return {
    'prepaid_balance':       (m) => prepaidBalance?.[m],
    'defrev_stripe':         (m) => defRevResult?.deferred_rev_stripe?.[m],
    'defrev_chargebee':      (m) => defRevResult?.deferred_rev_chargebee?.[m],
    'defrev_pa':             (m) => defRevResult?.deferred_revenue?.[m],
    'defrev_ae_quota':       (m) => defRevResult?.defrev_ae_quota?.[m],
    'defrev_company_quota':  (m) => defRevResult?.defrev_company_quota?.[m],
  };
}

function buildBSIsRef(isData, daResult, vendorOpex, prepaidBalance, defRevResult, MONTHS, implDrRefs) {
  const getters = refKeyGetters(prepaidBalance, defRevResult);
  /* W-081: a deferred implementation line LINKED to a real account -- 'defrev_impl_<id>' = the engine's deferred balance,
     read by computeBS's module_link driver for the leaf buildBSLines wired to that ref. Absent (every caller before W-081, and
     every tenant with no linked line) -> no extra key. */
  Object.entries(implDrRefs || {}).forEach(([k, series]) => { getters[k] = (m) => series?.[m]; });
  const ref = {
    total_revenue: {},
    total_cogs_sga: {},
    vendor_opex: {},
    monthly_depreciation: {},
    monthly_amortization: {},
    grossAdditions: daResult.grossAdditions || {},
    net_income_ytd: {},
  };
  // Initialize all module-link refKey buckets so computeBS lookups never miss
  Object.keys(getters).forEach(k => { ref[k] = {}; });

  MONTHS.forEach(m => {
    ref.total_revenue[m] = r2(isData.total_revenue?.[m]||0);
    ref.total_cogs_sga[m] = r2((isData.total_cogs?.[m]||0)+(isData.total_sga?.[m]||0));
    ref.vendor_opex[m] = r2(vendorOpex?.[m]||0);
    // computeDA's OWN two output keys (the accumulated-D&A roll), not IS leaves: literal by design (W-075).
    ref.monthly_depreciation[m] = r2(daResult.isLines?.other_depreciation?.[m]||0);
    ref.monthly_amortization[m] = r2(daResult.isLines?.other_amortization?.[m]||0);
    Object.entries(getters).forEach(([k, getter]) => {
      ref[k][m] = r2(getter(m) || 0);
    });
  });
  let ytd=0, py=null;
  MONTHS.forEach(m => {
    const [yr]=m.split("-");
    if(yr!==py){ytd=0;py=yr;}
    ytd=r2(ytd+(isData.net_income?.[m]||0));
    ref.net_income_ytd[m]=ytd;
  });

  // Phase 6: expose every IS line as a potential DSO/DPO base. The
  // bs_assumptions.base_is_line_id field lets users pick any IS line as
  // the divisor for a DSO/DPO driver. Don't overwrite the engine-internal
  // refs already populated above (total_revenue / total_cogs_sga /
  // vendor_opex / monthly_* / grossAdditions / net_income_ytd / module-
  // link getters). Each isData entry is a {month: value} map matching the
  // shape computeBS expects from IS_REF[baseKey].
  Object.entries(isData || {}).forEach(([k, v]) => {
    if (ref[k] !== undefined) return; // don't clobber engine-internal refs
    if (!v || typeof v !== 'object') return; // skip scalars (none today, defensive)
    ref[k] = v;
  });

  return ref;
}

function buildCFSIsRef(isData, MONTHS, daIds) {
  /* W-075: the D&A add-back sums daIds = daRouting.daLeafIds(isLines), computed once by runModel --
   * every IS leaf whose stored role is depreciation/amortization in 'ooe', plus both synthetics. It
   * replaced four literal keys (other_depreciation, other_amortization, oi_depreciation_expense,
   * oi_amortization_expense), which missed a D&A line named otherwise: a line named "Depreciation"
   * lost -58,138.31 of historical add-back (and its routed forecast too), and the balance check went
   * nonzero, so P9 blocked. A superset of the four, never narrower; forecast and history are disjoint
   * by month and each id is summed once. The same set feeds EBITDA (lineRoles via computeIS). */
  if (!Array.isArray(daIds)) throw new Error('buildCFSIsRef requires daIds (daRouting.daLeafIds(isLines))');
  const ref = { net_income_monthly:{}, da_monthly:{} };
  MONTHS.forEach(m => {
    ref.net_income_monthly[m] = r2(isData.net_income?.[m]||0);
    ref.da_monthly[m] = r2(daIds.reduce((s, id) => s + (isData[id]?.[m]||0), 0));
  });
  return ref;
}

export function runModel(cfg) {
  const { cutoff, actualsData, forecastData, bsActuals, bsAssumptions, dec23, openingMMBalance, sweepAct, oneoffs, finZero, sweepSettings, daResult, vendorOpex, prepaidBalance, defRevResult, fundraisingRounds, aeQuotaDR, companyQuotaDR, miscRevenueLines, implementationRevenue, implementationLines, allMonths, openingMonth, isLines, bsLines, cfsLines, revMappings, sweepInterestLineKey, accruedPayroll, accruedCommissions } = cfg;
  const MONTHS = allMonths || [];
  let curForecast = { ...forecastData };
  let curBsForecast = { ...(cfg.bsForecast||{}) };

  /* THE REVENUE INJECTION -- AE Quota GAAP at its target line, Company Quota GAAP at its target line, then each implementation line
   * at implTargetKey (its target or its own rev_impl_<id>; idempotent on the routed arm, the money deposited once at one key). Since
   * 2026-10-27 the keys and the series come from engines/revenueByLeaf.js revenueInjections -- the SAME function runFullModel reads
   * for a vendor's "% of revenue" base, so the IS and the base cannot disagree (Principle 0). The gates are the caller's: a null
   * aeQuotaDR / companyQuotaDR (an inactive module) or an unwired module injects nothing, as before.
   * (The N-A SITE 2 record -- a bare rev_impl_<id> once deposited a routed line TWICE, at two keys -- is why the implementation key
   *  is implTargetKey in one place: utils/implKeys.js, now called once, from revenueByLeaf.js.) */
  revenueInjections({ aeQuotaDR, companyQuotaDR, implementationRevenue, revMappings, months: MONTHS, cutoff }).forEach(([key, series]) => {
    if (!curForecast[key]) curForecast[key] = {};
    Object.entries(series).forEach(([m, v]) => { curForecast[key][m] = v; });
  });

  // ★★ SWEEP B4b (A8) — NO CHART OF ACCOUNTS => NO STATEMENTS.
  // computeBS/computeCFS now return honest-empty rather than computing on Semitek's
  // chart (their `|| BS_LINES` / `|| CFS_LINES` fallbacks are gone). The RECORD is
  // emitted here because neither engine's return shape can carry one: computeBS
  // returns a FLAT lineId->months map, so a `missingChartOfAccounts` key would
  // masquerade as a balance-sheet line. Emitting it at this level also avoids running
  // the convergence loop up to 20 times over nothing.
  // Fifth member of the warnings family (missingAnchorWarnings / missingDeptMappings /
  // missingSweepTarget / missingCloseMonth); reader lands with item 5.
  const missingChartOfAccounts = [];
  if (!bsLines || !cfsLines) {
    missingChartOfAccounts.push({
      reason: 'no_chart_of_accounts',
      hasBsLines: !!bsLines,
      hasCfsLines: !!cfsLines,
    });
    console.warn("[orchestrator] This company has no chart of accounts (bs_actuals_meta is empty) — the balance sheet and cash flow were NOT computed. They cannot be derived, and another company's chart is never a substitute. Complete onboarding Step 4.");
    return {
      isData: {}, bsData: {}, cfsData: {}, swData: {},
      iterations: 0, converged: false,
      frLines: [], implDeferredLines: [],
      missingSweepTarget: [], missingChartOfAccounts,
    };
  }

  // Phase 3b: derive sweep target BS line id from role rather than hardcoding.
  // total_clearing is a SYNTHETIC alwaysPresent line so its id is stable across
  // tenants — left hardcoded by design.
  //
  // ★ SWEEP B3a (A4) — FAIL LOUD. This used to fall back to 'chase_money_market',
  // SEMITEK'S OWN cash account, for any tenant whose chart has no role='sweep_target'
  // leaf. That is the worst shape available: on a tenant that HAS that key the sweep
  // balance lands in a real-but-wrong account; on one that does not (cid=3's target is
  // first_republic_bank) it books to a line nothing reads and the opening balance is a
  // silent $0. A sweep target is not derivable — it is an operator choice — so when it
  // is absent we book NO sweep into ANY account and record the gap.
  //
  // REACHABILITY: measured not-reached on cid=1/2/3 (all three have a sweep_target
  // leaf — diffs/sweep-b3-verify.txt). It IS reachable mid-onboarding, before Step 4
  // writes bs_actuals_meta — which is the state the tenant-#2 walk lives in.
  const sweepTargetId = bsLines?.find(l => l.role === 'sweep_target')?.id || null;
  // Sibling of missingAnchorWarnings / missingDeptMappings — ONE warnings pattern.
  const missingSweepTarget = [];
  if (!sweepTargetId) {
    missingSweepTarget.push({
      reason: 'no_sweep_target',
      bsLineCount: (bsLines || []).length,
    });
    console.warn("[orchestrator] No balance-sheet line has role='sweep_target' — the cash sweep is NOT booked to any account (a sweep target is an operator choice and cannot be inferred). Set the sweep account in onboarding Step 4.");
  }

  let prevMM = {};
  let iter = 0, done = false;
  let isData, bsData, cfsResult;

  // Build implementation deferred lines for BS/CFS injection
  const implDeferredLines = []; // { bsId, cfsId, label }
  const implDrRefs = {};        // W-081: { 'defrev_impl_<id>': deferredBalance } for the lines wired to a real account
  if (implementationRevenue) {
    Object.entries(implementationRevenue).forEach(([lineId, lineData]) => {
      if (lineData.recognition_mode === 'deferred') {
        /* W-081: a line the BUILDER wired to the client's real deferred-revenue account (bsLines carries
           module_link_ref 'defrev_impl_<id>' on that leaf) books its balance THERE, through IS_REF -- the account's history
           continues across the close (no 0 -> opening jump, no phantom cash). NO synthetic row for it. Asked of the BUILT
           skeleton (utils/implKeys.js implDrBsKey), so the orchestrator routes only where the builder wired -- the two cannot
           disagree. Unwired (Synthetic, unanswered, or an answer the builder could not wire) -> the synthetic, as before. */
        if (implDrBsKey(lineId, bsLines) !== implDrSyntheticKey(lineId)) {
          implDrRefs[implDrRefKey(lineId)] = lineData.deferredBalance || {};
          return;
        }
        const bsId = implDrSyntheticKey(lineId);
        implDeferredLines.push({
          bsId,
          cfsId: `chg_dr_impl_${lineId}`,
          label: `Deferred Revenue - ${lineData.name}`,
        });
        // Inject deferred balance into bsForecast
        curBsForecast[bsId] = {};
        MONTHS.forEach(m => {
          if (m > cutoff) {
            curBsForecast[bsId][m] = r2(lineData.deferredBalance?.[m] || 0);
          }
        });
      }
    });
  }

  /* BI-WEEKLY PAYROLL, STEP 3b: the accrued-payroll balance joins the same extra-refs map (computeBS's module_link arm reads
     IS_REF['accrued_payroll'] for the leaf the builder wired). ONLY when the BUILT skeleton carries the ref -- asked of the
     skeleton (engines/accruedPayroll.js accruedPayrollBuiltKey), so the orchestrator serves the series only where the builder
     wired it and the two cannot disagree. No series (every company that does not accrue bi-weekly payroll) -> no key. */
  if (accruedPayroll && accruedPayrollBuiltKey(bsLines)) implDrRefs[ACCRUED_PAYROLL_REF] = accruedPayroll;
  /* Commission ship 4: the accrued-commissions balance, by the same rule -- served only where the BUILT skeleton carries the ref
     (engines/accruedCommissions.js accruedCommissionsBuiltKey). No series (every company that does not accrue commissions) -> no key. */
  if (accruedCommissions && accruedCommissionsBuiltKey(bsLines)) implDrRefs[ACCRUED_COMMISSIONS_REF] = accruedCommissions;
  /* Billing terms P2 (p): the Unbilled-AR balance (the close less the cumulative cleared collections), by the same rule -- served only
     where the BUILT skeleton carries the ref (engines/billingTerms.js unbilledArBuiltKey). No series (every company that links no
     line) -> no key. NOTE: served through this extra-refs map rather than refKeyGetters, so a company without it gains no IS_REF key. */
  if (defRevResult && defRevResult.unbilled_ar && unbilledArBuiltKey(bsLines)) implDrRefs[UNBILLED_AR_REF] = defRevResult.unbilled_ar;

  // Inject fundraising rounds into bsForecast as dynamic equity lines
  const frLines = []; // { bsId, cfsId, label, type:'equity'|'cost' }
  if (fundraisingRounds && fundraisingRounds.length > 0) {
    fundraisingRounds.forEach(round => {
      const closeDate = round.close_date;
      const gross = parseFloat(round.gross_proceeds) || 0;
      const costs = parseFloat(round.issuance_costs) || 0;
      const safeName = round.round_name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
      const bsEquityId = `fr_eq_${round.id}_${safeName}`;
      const bsCostId = `fr_ic_${round.id}_${safeName}`;

      // Equity line: 0 before close, gross_proceeds from close month onward
      curBsForecast[bsEquityId] = {};
      curBsForecast[bsCostId] = {};
      MONTHS.forEach(m => {
        curBsForecast[bsEquityId][m] = m >= closeDate ? gross : 0;
        curBsForecast[bsCostId][m] = m >= closeDate ? r2(-costs) : 0; // costs are negative (contra-equity)
      });

      frLines.push({ bsId: bsEquityId, cfsId: `fin_${bsEquityId}`, label: round.round_name, type: 'equity' });
      if (costs > 0) {
        frLines.push({ bsId: bsCostId, cfsId: `fin_${bsCostId}`, label: `${round.round_name} Issuance Costs`, type: 'cost' });
      }
    });
  }

  const daIds = daLeafIds(isLines);   // W-075: the D&A leaves the CFS adds back, by stored role
  while (!done && iter < MAX_ITER) {
    iter++;
    isData = computeIS(cutoff, actualsData, curForecast, miscRevenueLines, implementationLines, MONTHS, isLines);
    const bsRef = buildBSIsRef(isData, daResult, vendorOpex, prepaidBalance, defRevResult, MONTHS, implDrRefs);
    bsData = computeBS(cutoff, bsAssumptions, bsActuals, curBsForecast, bsRef, frLines, implDeferredLines, MONTHS, bsLines);
    const cfsRef = buildCFSIsRef(isData, MONTHS, daIds);
    cfsResult = computeCFS(cutoff, sweepSettings, dec23, openingMMBalance, bsData, cfsRef, sweepAct, oneoffs, finZero, frLines, implDeferredLines, MONTHS, openingMonth, cfsLines);

    let maxD = 0;
    MONTHS.forEach(m => {
      if (m<=cutoff) return;
      const d = Math.abs(r2(cfsResult.sw.mm_e?.[m]||0) - r2(prevMM[m]||0));
      if (d>maxD) maxD=d;
    });

    /* COMPARE IN CENTS. maxD is a difference of two r2 values, so it is always a whole number of
       cents plus float noise. A genuine one-cent step evaluated to 0.009999997913837433, passed
       `< 0.01`, and ended the loop one iteration early -- WITHOUT the injection below, and there is
       no post-loop step, so the BS kept the previous iteration's cash while the CFS/swData kept the
       settled one. Measured in 11-16% of perturbed input states on all five tenants (phase 0,
       diffs/sweep-epsilon-phase0.txt). Rounding maxD to cents first makes "under a cent" mean
       exactly zero cents of change, which is what THRESHOLD always intended. THRESHOLD is unchanged. */
    if (iter>1 && r2(maxD)<THRESHOLD) { done=true; }
    else {
      const nf = { ...curForecast };
      /* ★ THE SWEEP-INTEREST TARGET — ONE EXPRESSION, so NULL (never answered) and the
       * 'oi_interest' SENTINEL ("keep a separate forecast line") take the SAME path and the
       * untested-branch count does not grow. NULL is every tenant until an operator confirms
       * at Step 9, and it reproduces today's behaviour exactly — which is what makes the
       * migration + server deploy window unable to move a number in either order.
       * ⛔ No name-guess here: the engine reads the stored answer or falls back. */
      const sweepInterestTarget = sweepInterestLineKey || 'oi_interest';
      /* ★ MONTH-GATED, and PRESERVING what the merged forecast already holds. Pointed at a
       * HISTORY-CARRYING leaf (the whole point of the fix), the old write-every-month form
       * would put explicit 0s over that key's ACTUALS months in the forecast blob — the misc
       * Path-A discipline verbatim ("Only forecast months are written, so the target key's
       * HISTORICAL actuals are NOT clobbered"), and the same gate the sibling BS injection
       * a few lines below already applies. Harmless for the synthetic (int_i is 0 in every
       * actuals month by construction, computeCFS:152-153); load-bearing for a real leaf. */
      nf[sweepInterestTarget] = { ...(curForecast[sweepInterestTarget] || {}) };
      MONTHS.forEach(m => {
        if (m <= cutoff) return;
        nf[sweepInterestTarget][m] = r2(-(cfsResult.sw.int_i?.[m]||0));
      });

      const nb = { ...curBsForecast };
      // ★ B3a (A4): with no sweep target the swept balance is routed NOWHERE. Writing
      // `nb[null]` would create a literal "null" BS key — a phantom account — so the
      // injection is skipped entirely. total_clearing still updates: it is the cash
      // rollup, not the sweep destination.
      if (sweepTargetId) nb[sweepTargetId] = {};
      nb.total_clearing = {};
      MONTHS.forEach(m => {
        if (m>cutoff) {
          if (sweepTargetId) nb[sweepTargetId][m] = r2(cfsResult.sw.mm_e?.[m]||0);
          nb.total_clearing[m] = r2(cfsResult.cfs.end_cash?.[m]||0);
        }
      });

      prevMM = {};
      MONTHS.forEach(m => { prevMM[m]=r2(cfsResult.sw.mm_e?.[m]||0); });
      curForecast = nf;
      curBsForecast = nb;
    }
  }

  return { isData, bsData, cfsData:cfsResult.cfs, swData:cfsResult.sw, iterations:iter, converged:done, frLines, implDeferredLines, missingSweepTarget, missingChartOfAccounts };
}

export { r2, buildBSIsRef, buildCFSIsRef };
